import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, MenuItem, Paper, TextField } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useFormik } from "formik";
import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import {
  Callout,
  ChoiceToggle,
  ConsentRow,
  CountrySelect,
  FieldWrapper,
  StepActions,
  PageHeader,
  SectionHeader,
} from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setShareHolderData, setShareHolderRfcCurp } from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetStatesQuery, useLazyGetPersonTypeQuery } from "@api/catalogs";
import { useLazyGetCurpQuery, useSaveShareholderDataMutation } from "@api/onboarding";
import { usePatchPersonMutation } from "@api/personasApi";
import { usePostValidateNextStageMutation } from "@api/applications";
import {
  convertSex,
  extractRFCFromCurp,
  substractDateFromCURP,
  substractDateFromRFC,
  validateDates,
  transformTitleCase,
} from "@helpers/funciones";
import { CURP, EMAIL, PHONE_NUMBER, RFC_HOMOCLAVE, RFC_PFAE_SIN_CLAVE } from "@utils/expreciones";
import { PERSON_CATALOG, STATUS_CATALOG } from "@helpers/constantes";
import { CurpQuery, PayloadPersonPatch, PayloadSaveShareholderData } from "@interfaces/request";
import { ResponseSaveShareholderData, ApiError } from "@interfaces/response";
import { StateStructure } from "@interfaces/catalogs";
import useStatusConfig from "@hooks/useStatusConfig";

const MAX_DATE = dayjs().subtract(18, "years");
const ERROR_INVALID_RFC_DATE = "La fecha de nacimiento del RFC no coincide con la registrada arriba";
const ERROR_INVALID_CURP_DATE = "La fecha de nacimiento del CURP no coincide con la registrada arriba";

export const DatosAccionista = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  // Dos transiciones de status encadenadas: esta pantalla fusiona dos pasos legacy — DatosAccionista
  // (CSDAL) y RfcCurp (CSCDA) — que en el legacy corren en pantallas separadas, una después de la otra.
  const { nextStatus: nextStatusDatos, previousStatus } = useStatusConfig(STATUS_CATALOG.datos_accionista.code);
  const { nextStatus: nextStatusRfcCurp } = useStatusConfig(STATUS_CATALOG.rfc_curp.code);

  const application = useSelector((state: RootState) => state.register.application);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const shareholderData = useSelector((state: RootState) => state.register.shareholder);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const buroNip = useSelector((state: RootState) => state.register.buroNip);
  const addressData = useSelector((state: RootState) => state.register.address_data);

  const [getStatesTrigger] = useLazyGetStatesQuery();
  const [getCurp] = useLazyGetCurpQuery();
  const [saveShareholderData] = useSaveShareholderDataMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [getPersonTypeTrigger] = useLazyGetPersonTypeQuery();

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [personTypeId, setPersonTypeId] = useState("");
  const [shareholderId, setShareholderId] = useState("");
  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [isFetchingCurp, setIsFetchingCurp] = useState(false);
  const [placesCatalog, setPlacesCatalog] = useState<StateStructure[]>([]);
  const [showInvalidDatesAlert, setShowInvalidDatesAlert] = useState(false);
  const curpAutoFetchedRef = useRef(false);

  const formik = useFormik({
    initialValues: {
      majority_shareholder: false,
      name: shareholderData.name || "",
      first_lastname: shareholderData.first_lastname || "",
      second_lastname: shareholderData.second_lastname || "",
      date_birth: "",
      birth_state: shareholderData.birth_state || "",
      email: shareholderData.email || "",
      sex: shareholderData.sex || "",
      code_phone_number: shareholderData.code_phone_number || "+52",
      phone_number: shareholderData.phone_number || "",
      rfc: shareholderData.rfc || "",
      homoclave: shareholderData.rfc_homoclave || "",
      curp: shareholderData.curp || "",
      confirmCheck: false,
      confirmAuthorization: false,
    },
    validationSchema: yup.object().shape({
      name: yup.string().required("Campo requerido"),
      first_lastname: yup.string().required("Campo requerido"),
      second_lastname: yup.string(),
      date_birth: yup
        .string()
        .required("Campo requerido")
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .test("is-adult", "Debes ser mayor de 18 años", (value) => {
          if (!value) return false;
          const birthDate = dayjs(value);
          const age = dayjs().diff(birthDate, "years");
          return age >= 18;
        }),
      birth_state: yup.string().required("Campo requerido"),
      email: yup
        .string()
        .required("Campo requerido")
        .matches(EMAIL, "Asegúrate de incluir el @ y los puntos necesarios (.com .mx)"),
      sex: yup.string().oneOf(["MASCULINO", "FEMENINO"], "Debe seleccionar un género").required("Campo requerido"),
      code_phone_number: yup.string().required("Campo requerido"),
      phone_number: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
      rfc: yup
        .string()
        .required("Campo requerido")
        .matches(RFC_PFAE_SIN_CLAVE, "Formato de RFC no válido")
        .test("fecha-nacimiento-rfc", ERROR_INVALID_RFC_DATE, function (value) {
          if (!value) return true;
          const date_part = substractDateFromRFC(value);
          return validateDates(this.parent.date_birth, date_part);
        }),
      homoclave: yup.string().required("Campo requerido").matches(RFC_HOMOCLAVE, "Formato de homoclave no válido"),
      curp: yup
        .string()
        .required("Campo requerido")
        .matches(CURP, "Formato de CURP no válido")
        .test("fecha-nacimiento-curp", ERROR_INVALID_CURP_DATE, function (value) {
          if (!value) return true;
          const date_part = substractDateFromCURP(value);
          return validateDates(this.parent.date_birth, date_part);
        }),
      confirmCheck: yup.boolean().isTrue().required(""),
      confirmAuthorization: yup.boolean().isTrue().required(""),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      try {
        if (!nextStatusDatos) {
          throw new Error("No se encontró el siguiente status");
        }
        // Esta pantalla junta datos del accionista (CSDAL) y RFC/CURP (CSCDA). Si el `status_config` del
        // producto pasa por CSCDA se avanzan los dos; si no (p. ej. Flux: CSDAL → CSDAV), solo uno.
        const pasaPorRfcCurp = nextStatusDatos.code === STATUS_CATALOG.rfc_curp.code;
        if (pasaPorRfcCurp && !nextStatusRfcCurp) {
          throw new Error("No se encontró el siguiente status");
        }

        setSubmitting(true);

        let id = shareholderId;

        if (shareholderId) {
          // si ya se lleno el formulario de domicilio, y se modifica la fecha de nacimiento, se debe validar la dirección.
          let address_verification_required = false;
          if (addressData && addressData.id) {
            const previus_date_birth = shareholderData.date_birth;
            const new_date_birth = values.date_birth;
            address_verification_required = previus_date_birth !== new_date_birth;
          }

          const payloadShareholderData: PayloadPersonPatch = {
            person_types: [personTypeId],
            name: values.name,
            last_name: values.first_lastname,
            last_name_2: values.second_lastname,
            birth_date: dayjs(values.date_birth).format("YYYY-MM-DD"),
            birth_state: Number(values.birth_state),
            email: values.email,
            sex: values.sex,
            rfc: "",
            curp: "",
            loan_application_id: application.id,
            code_phone_number: values.code_phone_number,
            phone_number: values.phone_number,
          };

          await patchPerson({ personId: shareholderId, body: payloadShareholderData }).unwrap();

          dispatch(
            setShareHolderData({
              id: shareholderId,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              date_birth: values.date_birth,
              birth_state: values.birth_state,
              email: values.email,
              sex: values.sex,
              rfc: "",
              rfc_homoclave: "",
              curp: "",
              bureau_inquiry: {
                prestamo_hipotecario: false,
                credito_auto: false,
                tarjeta_credito: false,
                tarjeta: "",
                autorizacion_buro: false,
              },
              code_phone_number: values.code_phone_number,
              phone_number: values.phone_number,
              address_verification_required,
            }),
          );
        } else {
          const payloadShareholderData: PayloadSaveShareholderData = {
            person_types: [personTypeId],
            name: values.name,
            last_name: values.first_lastname,
            last_name_2: values.second_lastname,
            birth_date: dayjs(values.date_birth).format("YYYY-MM-DD"),
            birth_state: Number(values.birth_state),
            email: values.email,
            sex: values.sex,
            rfc: "",
            curp: "",
            loan_application_id: application.id,
            is_shareholder: true,
            is_aval_onboarding: true,
            code_phone_number: values.code_phone_number,
            phone_number: values.phone_number,
          };

          const responseShareholderData: ResponseSaveShareholderData =
            await saveShareholderData(payloadShareholderData).unwrap();

          id = responseShareholderData.data.id;

          dispatch(
            setShareHolderData({
              id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              date_birth: values.date_birth,
              birth_state: values.birth_state,
              email: values.email,
              sex: values.sex,
              rfc: "",
              rfc_homoclave: "",
              curp: "",
              bureau_inquiry: {
                prestamo_hipotecario: false,
                credito_auto: false,
                tarjeta_credito: false,
                tarjeta: "",
                autorizacion_buro: false,
              },
              code_phone_number: values.code_phone_number,
              phone_number: values.phone_number,
            }),
          );
        }

        // === RfcCurp.tsx: guarda RFC/CURP sobre el mismo registro de persona ===
        await patchPerson({
          personId: id,
          body: { rfc: values.rfc + values.homoclave, curp: values.curp },
        }).unwrap();

        dispatch(
          setShareHolderRfcCurp({
            rfc: values.rfc,
            rfc_homoclave: values.homoclave,
            curp: values.curp,
          }),
        );

        await triggerPostNextStage({
          applicationId: application.id,
          statusCode: nextStatusDatos.code,
          username: `TOFU - ${accountUser.email}`,
        }).unwrap();

        if (pasaPorRfcCurp && nextStatusRfcCurp && !shareholderData.check_sat_bureau) {
          await triggerPostNextStage({
            applicationId: application.id,
            statusCode: nextStatusRfcCurp.code,
            username: `TOFU - ${accountUser.email}`,
          }).unwrap();
        }

        navigate((pasaPorRfcCurp ? nextStatusRfcCurp?.url : nextStatusDatos.url) || "");
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status === 409) {
          formik.setFieldError("email", "El correo electrónico ya se encuentra registrado");
          return;
        }

        Sentry.captureException(error, { extra: { component: "DatosAccionista", values: formik.values } });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { setFieldValue, validateForm } = formik;
  /** Solo de tipo: tipar con `typeof formik.values` dentro del callback hacía que eslint pidiera `formik` como dependencia. */
  type AccionistaFormValues = typeof formik.values;
  const isFieldDisabled = formik.isSubmitting || shareholderData.check_sat_bureau;
  const isPhoneDisabled = isFieldDisabled || buroNip.isValid;
  const isRfcCurpDisabled = isFetchingCurp || isFieldDisabled;

  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoadingInitial(true);
      const [responsePersonType, responseStates] = await Promise.all([
        getPersonTypeTrigger().unwrap(),
        getStatesTrigger().unwrap(),
      ]);

      const personTypeData = responsePersonType.find(
        (item) => item.code === PERSON_CATALOG.accionista_mayoritario.code,
      );
      setPersonTypeId(String(personTypeData?.id));
      setPlacesCatalog(responseStates);
      setTimeout(() => {
        validateForm();
      }, 200);
    } catch (error) {
      console.error("Ha ocurrido un error al obtener los datos iniciales", error);
    } finally {
      setIsLoadingInitial(false);
    }
  }, [getPersonTypeTrigger, getStatesTrigger, validateForm]);

  const fetchCurp = useCallback(
    async (values: AccionistaFormValues) => {
      const { name, first_lastname, second_lastname, birth_state, date_birth, sex } = values;

      try {
        setIsFetchingCurp(true);

        const birthStateFormatted = Number(birth_state).toString().padStart(2, "0");

        const payloadGetCurp: CurpQuery = {
          nombres: name,
          apellido_paterno: first_lastname,
          apellido_materno: second_lastname,
          estado_nacimiento: birthStateFormatted,
          fecha_nacimiento: dayjs(date_birth, "YYYY-MM-DD").format("DD-MM-YYYY"),
          sexo: convertSex(sex),
        };
        const responseGetCurp = await getCurp(payloadGetCurp).unwrap();
        const rfc = extractRFCFromCurp(responseGetCurp.data.curp);
        await setFieldValue("curp", responseGetCurp.data.curp, false);
        await setFieldValue("rfc", rfc, false);
        await validateForm();
      } catch (error) {
        console.error("Hubo un error al obtener la CURP: ", error);
      } finally {
        setIsFetchingCurp(false);
      }
    },
    [getCurp, setFieldValue, validateForm],
  );

  const handleMajorityShareholderChange = (checked: boolean) => {
    if (checked) {
      formik.setFieldValue("name", clientDetails.name);
      formik.setFieldValue("first_lastname", clientDetails.first_lastname);
      formik.setFieldValue("second_lastname", clientDetails.second_lastname);
      formik.setFieldValue("email", accountUser.email);
      formik.setFieldValue("code_phone_number", clientDetails.country_code || "+52");
      formik.setFieldValue("phone_number", clientDetails.phone);
    } else {
      formik.setFieldValue("name", "");
      formik.setFieldValue("first_lastname", "");
      formik.setFieldValue("second_lastname", "");
      formik.setFieldValue("email", "");
      formik.setFieldValue("code_phone_number", "+52");
      formik.setFieldValue("phone_number", "");
    }
  };

  const handleUppercaseChange = (field: "rfc" | "homoclave" | "curp") => (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const value = e.target.value.toUpperCase();
    const start = input.selectionStart;
    formik.handleChange(e);
    formik.setFieldValue(field, value);
    setTimeout(() => input.setSelectionRange(start, start), 0);
  };

  const syncShareholderFields = useCallback(async () => {
    if (shareholderData?.id !== "" && shareholderData?.id !== undefined) {
      setShareholderId(String(shareholderData.id));
      // Ya registrado: la casilla de "Reutilizar mis datos" queda bloqueada; se muestra marcada si el
      // accionista guardado tiene los mismos datos que el usuario.
      const same = (a?: string, b?: string) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
      const reutilizoDatos =
        same(shareholderData.name, clientDetails.name) &&
        same(shareholderData.first_lastname, clientDetails.first_lastname) &&
        same(shareholderData.email, accountUser.email);
      setFieldValue("majority_shareholder", reutilizoDatos, false);
      setSelectedDate(dayjs(shareholderData.date_birth));
      await setFieldValue("date_birth", shareholderData.date_birth, false);
    }
    setFieldValue("confirmCheck", Boolean(shareholderData.check_sat_bureau), false);
    setFieldValue("confirmAuthorization", Boolean(shareholderData.check_sat_bureau), false);
    setTimeout(() => {
      validateForm();
    }, 200);
  }, [
    shareholderData,
    setFieldValue,
    validateForm,
    clientDetails.name,
    clientDetails.first_lastname,
    accountUser.email,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    syncShareholderFields();
  }, [syncShareholderFields]);

  useEffect(() => {
    const { name, first_lastname, second_lastname, birth_state, date_birth, sex } = formik.values;
    const readyToFetch = name && first_lastname && second_lastname && birth_state && date_birth && sex;
    if (!readyToFetch || curpAutoFetchedRef.current) return;

    curpAutoFetchedRef.current = true;
    fetchCurp(formik.values);
  }, [formik.values, fetchCurp]);

  useEffect(() => {
    const is_invalid_rfc = formik.errors.rfc === ERROR_INVALID_RFC_DATE;
    const is_invalid_curp = formik.errors.curp === ERROR_INVALID_CURP_DATE;
    setShowInvalidDatesAlert(is_invalid_rfc || is_invalid_curp);
  }, [formik.errors]);

  return (
    <Box>
      <PageHeader
        eyebrow="Accionista mayoritario"
        title="Registro del accionista mayoritario"
        subtitle={
          <>
            Registra al socio con mayor participación en la empresa.
            <br />
            Si hay empate, elige a quien sea representante legal o tenga el puesto más alto. Si los socios son empresas
            o el mayoritario es extranjero, registra al representante legal.
          </>
        }
        fullWidthSubtitle
      />

      <Box sx={{ mb: 2 }}>
        <ConsentRow
          tone="info"
          checked={formik.values.majority_shareholder}
          onChange={(checked) => {
            formik.setFieldValue("majority_shareholder", checked);
            handleMajorityShareholderChange(checked);
          }}
          // Con el accionista ya registrado no se permite: desmarcarla borraría sus datos del formulario.
          disabled={isFieldDisabled || Boolean(shareholderId)}
          label={
            <>
              <strong>Reutilizar mis datos de usuario.</strong> Se llenarán con la información que ya registraste
            </>
          }
        />
      </Box>

      <Paper variant="lendia_paper">
        <SectionHeader label="Datos personales" first />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
          <FieldWrapper label="Nombre(s)" error={(formik.touched.name && formik.errors.name) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="name"
              value={formik.values.name}
              onChange={(e) => formik.setFieldValue("name", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Nombre(s)"
              inputProps={{ maxLength: 100 }}
              disabled={isFieldDisabled}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Apellido paterno"
            error={(formik.touched.first_lastname && formik.errors.first_lastname) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="first_lastname"
              value={formik.values.first_lastname}
              onChange={(e) => formik.setFieldValue("first_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Apellido paterno"
              inputProps={{ maxLength: 100 }}
              disabled={isFieldDisabled}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Apellido materno"
            optionalSuffix="(opcional)"
            error={(formik.touched.second_lastname && formik.errors.second_lastname) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="second_lastname"
              value={formik.values.second_lastname}
              onChange={(e) => formik.setFieldValue("second_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              placeholder="Apellido materno"
              inputProps={{ maxLength: 100 }}
              disabled={isFieldDisabled}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Fecha de nacimiento"
            error={(formik.touched.date_birth && formik.errors.date_birth) || ""}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
              <DatePicker
                value={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);
                  const formattedDate = dayjs(date).format("YYYY-MM-DD");
                  formik.setFieldValue("date_birth", formattedDate);
                  formik.validateField("date_birth");
                }}
                maxDate={MAX_DATE}
                slotProps={{
                  textField: {
                    size: "lendia_input",
                    fullWidth: true,
                    name: "date_birth",
                    onBlur: formik.handleBlur,
                  },
                }}
                disabled={isFieldDisabled}
              />
            </LocalizationProvider>
          </FieldWrapper>
          <FieldWrapper
            label="Lugar de nacimiento"
            error={(formik.touched.birth_state && formik.errors.birth_state) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              select
              fullWidth
              name="birth_state"
              // Muestra "Selecciona…" mientras no hay valor, como el `<select>` del diseño.
              SelectProps={{ displayEmpty: true }}
              value={formik.values.birth_state}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              disabled={isFieldDisabled || isLoadingInitial}
            >
              <MenuItem value="">Selecciona…</MenuItem>
              {placesCatalog.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </FieldWrapper>
          <FieldWrapper label="Sexo" error={(formik.touched.sex && formik.errors.sex) || ""}>
            <ChoiceToggle
              value={formik.values.sex}
              onChange={(v) => formik.setFieldValue("sex", v)}
              options={[
                { value: "FEMENINO", label: "Femenino" },
                { value: "MASCULINO", label: "Masculino" },
              ]}
              disabled={isFieldDisabled}
            />
          </FieldWrapper>
          <FieldWrapper label="Correo electrónico" full error={(formik.touched.email && formik.errors.email) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              name="email"
              value={formik.values.email}
              onChange={(e) => formik.setFieldValue("email", (e.target.value || "").toLowerCase())}
              onBlur={formik.handleBlur}
              placeholder="correo@empresa.com"
              inputProps={{ maxLength: 100 }}
              disabled={formik.isSubmitting}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Teléfono celular"
            full
            hint="A este número enviaremos el código OTP para autorización de consulta buró."
            error={(formik.touched.phone_number && formik.errors.phone_number) || ""}
          >
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={formik.values.code_phone_number}
                onChange={(v) => {
                  if (buroNip.isValid) return;
                  formik.setFieldValue("code_phone_number", v);
                }}
                disabled={isPhoneDisabled}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="phone_number"
                value={formik.values.phone_number}
                onChange={(e) => {
                  if (buroNip.isValid) return;
                  // Solo dígitos: se descarta cualquier otro carácter (también al pegar).
                  formik.setFieldValue("phone_number", e.target.value.replace(/\D/g, ""));
                }}
                onBlur={formik.handleBlur}
                placeholder="55 1234 5678"
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
                disabled={isPhoneDisabled}
              />
            </Box>
          </FieldWrapper>
        </Box>

        <SectionHeader label="Identificación fiscal" />
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {showInvalidDatesAlert && (
            <Callout tone="warn">
              El RFC o CURP no coinciden con la fecha de nacimiento capturada arriba — verifícalos.
            </Callout>
          )}

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: "12px 14px" }}>
            <FieldWrapper
              label="RFC"
              hint="Se prellena automáticamente a partir de tu CURP."
              error={(formik.touched.rfc && formik.errors.rfc) || ""}
            >
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="rfc"
                value={formik.values.rfc}
                onChange={handleUppercaseChange("rfc")}
                onBlur={formik.handleBlur}
                inputProps={{ maxLength: 10, style: { textTransform: "uppercase" } }}
                disabled={isRfcCurpDisabled}
              />
            </FieldWrapper>
            <FieldWrapper
              label="Homoclave"
              hint="Los últimos 3 caracteres de tu RFC."
              error={(formik.touched.homoclave && formik.errors.homoclave) || ""}
            >
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="homoclave"
                value={formik.values.homoclave}
                onChange={handleUppercaseChange("homoclave")}
                onBlur={formik.handleBlur}
                placeholder="PR1"
                inputProps={{ maxLength: 3, style: { textTransform: "uppercase" } }}
                disabled={isRfcCurpDisabled}
              />
            </FieldWrapper>
            <FieldWrapper label="CURP" full error={(formik.touched.curp && formik.errors.curp) || ""}>
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                name="curp"
                value={formik.values.curp}
                onChange={handleUppercaseChange("curp")}
                onBlur={formik.handleBlur}
                placeholder="CURP"
                inputProps={{ maxLength: 18, style: { textTransform: "uppercase" } }}
                disabled={isRfcCurpDisabled}
              />
            </FieldWrapper>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        {/* Como el `.callout` del primer check: fondo y borde de aviso de la marca y check en su color. */}
        <ConsentRow
          tone="info"
          checked={formik.values.confirmCheck}
          onChange={(checked) => formik.setFieldValue("confirmCheck", checked)}
          disabled={isRfcCurpDisabled}
          label={<strong>Confirmo que el RFC y CURP son correctos.</strong>}
        />

        <ConsentRow
          tone="info"
          checked={formik.values.confirmAuthorization}
          onChange={(checked) => formik.setFieldValue("confirmAuthorization", checked)}
          disabled={formik.isSubmitting}
          label={
            <>
              <strong>Declaro bajo protesta de decir verdad ser el accionista mayoritario</strong> de la empresa
              mencionada en esta solicitud.
            </>
          }
        />
      </Box>

      <StepActions
        onBack={() => {
          if (previousStatus) navigate(previousStatus.url);
        }}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || formik.isSubmitting}
      />
    </Box>
  );
};
